import dayjs from 'dayjs';

import type { PromoFieldConfig, PromoFieldTransform } from '../config/tpoGenConfig';
import type { FieldRule } from '../config/fieldRulesConfig';
import type { EntityAttribute } from '../types/config';
import { buildOfferPayload, buildOfferResponseToFormValues } from './buildOfferPayload';
import { buildAttributesPayload, readAttributesToForm } from './entityAttributes';

export const TRANSFORMS: Record<PromoFieldTransform, (v: unknown) => unknown> = {
    trim: (v) => (typeof v === 'string' ? v.trim() : v),
    momentToDateString: (v) => {
        if (!v) return '';
        if (typeof v === 'string') return v;
        return (v as { format: (f: string) => string }).format('YYYY-MM-DD');
    },
    parseToDayjs: (v) => (v ? dayjs(v as string) : undefined),
};

const ATTR_PREFIX = 'attributes.';

const isAttributePath = (key: string | null): boolean => typeof key === 'string' && key.startsWith(ATTR_PREFIX);

const splitPath = (key: string): string[] => key.split('.');

const isEmpty = (v: unknown) => v === undefined || v === null || v === '';

const setNested = (root: Record<string, unknown>, path: string[], value: unknown): void => {
    let cur: Record<string, unknown> = root;
    for (let i = 0; i < path.length - 1; i++) {
        const seg = path[i];
        if (cur[seg] === null || typeof cur[seg] !== 'object') cur[seg] = {};
        cur = cur[seg] as Record<string, unknown>;
    }
    cur[path[path.length - 1]] = value;
};

// Writes null at the parent of the leaf path.
// If two fields share a parent, the order in PROMO_FIELDS matters — a later
// parentNull would clobber an earlier write.
const setParentNull = (root: Record<string, unknown>, path: string[]): void => {
    if (path.length === 1) {
        root[path[0]] = null;
        return;
    }
    let cur: Record<string, unknown> = root;
    for (let i = 0; i < path.length - 2; i++) {
        const seg = path[i];
        if (cur[seg] === null || typeof cur[seg] !== 'object') cur[seg] = {};
        cur = cur[seg] as Record<string, unknown>;
    }
    cur[path[path.length - 2]] = null;
};

const getNested = (root: Record<string, unknown>, path: string[]): unknown => {
    let cur: unknown = root;
    for (const seg of path) {
        if (cur === null || cur === undefined || typeof cur !== 'object') return undefined;
        cur = (cur as Record<string, unknown>)[seg];
    }
    return cur;
};

export function buildPromoPayload(
    entityAttributes: EntityAttribute[],
    fields: PromoFieldConfig[],
    formValues: Record<string, unknown>,
    // Cross-scope fallback source for fields with `valueFrom` (e.g. the rule
    // store's global values, keyed by qualified id `'event:eventName'`). Defaults
    // to empty so customers without any `valueFrom` field are unaffected.
    externalValues: Record<string, unknown> = {},
): Record<string, unknown> {
    const payload: Record<string, unknown> = {};
    for (const field of fields) {
        if (field.apiKey === null) continue;
        // The nested `attributes` object is owned by the codec below.
        if (isAttributePath(field.apiKey)) continue;
        const path = splitPath(field.apiKey);
        let raw = formValues[field.name];
        if (isEmpty(raw) && field.valueFrom) raw = externalValues[field.valueFrom];
        if (isEmpty(raw)) {
            switch (field.onEmpty) {
                case 'emptyString':
                    setNested(payload, path, '');
                    break;
                case 'parentNull':
                    setParentNull(payload, path);
                    break;
                case 'omit':
                default:
                    break;
            }
            continue;
        }
        const value = field.transform ? TRANSFORMS[field.transform](raw) : raw;
        setNested(payload, path, value);
    }

    payload.attributes = buildAttributesPayload(entityAttributes, fields, formValues, 'promotion', TRANSFORMS);
    payload.offer = buildOfferPayload(fields, formValues);

    return payload;
}

export function buildPromoFormValues(
    entityAttributes: EntityAttribute[],
    fields: PromoFieldConfig[],
    data: Record<string, unknown>,
    rules: FieldRule[],
): Record<string, unknown> {
    const values: Record<string, unknown> = {};
    for (const field of fields) {
        if (field.responseKey === null) continue;
        // The nested `attributes` object is owned by the codec below.
        if (isAttributePath(field.responseKey)) continue;
        const raw = getNested(data, splitPath(field.responseKey));
        if (raw === undefined || raw === null) continue;
        values[field.name] = field.responseTransform ? TRANSFORMS[field.responseTransform](raw) : raw;
    }

    Object.assign(
        values,
        readAttributesToForm(
            entityAttributes,
            fields,
            data.attributes as Record<string, unknown> | null | undefined,
            'promotion',
            TRANSFORMS,
        ),
    );

    const offerResponse = data.offer as Record<string, unknown> | null | undefined;
    if (offerResponse) {
        const offerFormValues = buildOfferResponseToFormValues(fields, offerResponse, rules);
        Object.assign(values, offerFormValues);
    }

    return values;
}
